<template>
    <div class="dashboard-body-futuristic">
        <main class="futuristic-container">
            <AppSidebar :activeItem="sidebarActive" />

            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="dl-wrapper">

                    <!-- Header -->
                    <div class="dl-header">
                        <div class="dl-header-left">
                            <div class="dl-header-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M9 11l3 3L22 4" />
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                </svg>
                            </div>
                            <div>
                                <h1 class="dl-title">Deliverables</h1>
                                <p class="dl-subtitle">Track contractor deliverables &amp; milestones by their deadlines.</p>
                            </div>
                        </div>
                        <div class="dl-progress" v-if="!loading">
                            <div class="dl-progress-ring">
                                <span class="dl-progress-num">{{ completedCount }}/{{ totalCount }}</span>
                            </div>
                            <div class="dl-progress-bar-wrap">
                                <div class="dl-progress-bar" :style="{ width: progressPercent + '%' }"></div>
                            </div>
                            <span class="dl-progress-label">{{ progressPercent }}% complete</span>
                        </div>
                    </div>

                    <!-- Reference points bar: every anchor date (LOA, Appointed Date,
                         each FMI-n) the deliverables depend on — set them to compute due dates. -->
                    <div v-if="!loading && items.length && referencePoints.length" class="dl-dates-bar">
                        <div class="dl-dates-info">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            <span>Enter the actual date of each reference point below. All deliverable due dates are calculated from these.</span>
                        </div>
                        <div class="dl-dates-fields">
                            <label v-for="rp in referencePoints" :key="rp.key" class="dl-date-field">
                                <span>{{ rp.label }}</span>
                                <input type="date" v-model="rp.date" :disabled="!isAdmin" />
                            </label>
                            <button v-if="isAdmin" class="dl-date-save" :disabled="savingDates" @click="saveReferencePoints">
                                {{ savingDates ? 'Applying…' : 'Apply & Recompute' }}
                            </button>
                        </div>
                    </div>

                    <!-- Loading -->
                    <div v-if="loading" class="dl-loading">
                        <div class="dl-spinner"></div>
                        <p>Loading deliverables…</p>
                    </div>

                    <!-- Load error -->
                    <div v-else-if="loadError" class="dl-empty is-error" role="alert">
                        {{ loadError }}
                        <button type="button" class="dl-retry" @click="fetchDeliverables">Try Again</button>
                    </div>

                    <!-- Empty -->
                    <div v-else-if="!items.length" class="dl-empty">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                        No deliverables extracted for this bid yet. They appear once the analysis finishes.
                    </div>

                    <!-- Grouped checklist -->
                    <template v-else>
                        <div v-for="(rows, category) in grouped" :key="category" class="dl-group">
                            <h3 class="dl-group-title">{{ category }}</h3>

                            <div class="dl-table">
                                <div class="dl-table-head">
                                    <span class="col-check">Done</span>
                                    <span class="col-head">Deliverable</span>
                                    <span class="col-deadline">Deadline</span>
                                    <span class="col-due">Due Date</span>
                                    <span class="col-actual">Actual Delivery</span>
                                    <span class="col-remarks">Remarks</span>
                                    <span class="col-action"></span>
                                </div>

                                <div v-for="item in rows" :key="item.id" class="dl-row"
                                    :class="{ 'is-done': item.completed, 'is-overdue': isOverdue(item), 'is-soon': isDueSoon(item) }">
                                    <!-- Checkbox -->
                                    <div class="col-check">
                                        <label class="dl-checkbox">
                                            <input type="checkbox" :checked="!!item.completed"
                                                :disabled="$isViewOnly()" :aria-label="'Done: ' + item.head"
                                                @change="toggleCompleted(item, $event)" />
                                            <span class="dl-checkmark">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" stroke-width="3.5" stroke-linecap="round"
                                                    stroke-linejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            </span>
                                        </label>
                                    </div>

                                    <!-- Deliverable head -->
                                    <div class="col-head">
                                        <span class="dl-head-text">{{ item.head }}</span>
                                    </div>

                                    <!-- Raw deadline expr -->
                                    <div class="col-deadline">
                                        <span class="dl-chip">{{ item.deadline_expr || '—' }}</span>
                                    </div>

                                    <!-- Resolved due date -->
                                    <div class="col-due">
                                        <span v-if="item.due_date" class="dl-due" :class="dueClass(item)">
                                            {{ formatDate(item.due_date) }}
                                            <small v-if="!item.completed" class="dl-due-note">{{ dueNote(item) }}</small>
                                        </span>
                                        <span v-else class="dl-due muted">Not resolved</span>
                                    </div>

                                    <!-- Actual delivery date (editable, defaults to today) -->
                                    <div class="col-actual">
                                        <input type="date" class="dl-actual-input" v-model="item.actual_delivery_date" :disabled="$isViewOnly()"
                                            :aria-label="'Actual delivery date for ' + item.head" />
                                    </div>

                                    <!-- Remarks -->
                                    <div class="col-remarks">
                                        <textarea class="dl-remarks-input" rows="2" placeholder="Add a remark…"
                                            :aria-label="'Remarks for ' + item.head"
                                            v-model="item.remarks" :disabled="$isViewOnly()"></textarea>
                                    </div>

                                    <!-- Save -->
                                    <div class="col-action">
                                        <button class="dl-save-btn admin-only" :disabled="savingId === item.id"
                                            @click="saveItem(item)">
                                            <svg v-if="savingId !== item.id" width="15" height="15" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                                <polyline points="17 21 17 13 7 13 7 21" />
                                                <polyline points="7 3 7 8 15 8" />
                                            </svg>
                                            <svg v-else class="dl-spin" width="15" height="15" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" stroke-width="2.5">
                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                            </svg>
                                            {{ savingId === item.id ? 'Saving…' : 'Save' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </template>

                    <div v-if="!loading" class="dl-footer">
                        <button class="dl-back" @click="goBack">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2.5" aria-hidden="true">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            Back
                        </button>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import { isAdmin as userIsAdmin } from '@/utils/auth';
import { notify } from '@/utils/notify';

export default {
    name: 'Deliverables',
    components: { AppSidebar },
    props: { getAccessToken: Function },
    data() {
        return {
            loading: true,
            savingId: null,
            savingDates: false,
            loadError: '',
            items: [],
            grouped: {},
            referencePoints: [],   // [{key, label, date}] — every anchor the deliverables use
        };
    },
    computed: {
        pdfId() { return this.$route.params.pdf_id; },
        sidebarActive() {
            // Keep the sidebar on the section we came from. Prefer an explicit
            // ?from (the exact originating menu item); otherwise fall back to the
            // bid status: COMPLETED -> "Completed", WIN -> "Active Projects", else "Bids".
            const from = String(this.$route.query.from || '').trim();
            const allowed = ['tenders', 'bookmarks', 'files', 'upload', 'active', 'completed', 'knowledge', 'admin', 'dashboard'];
            if (allowed.includes(from)) return from;
            const s = String(this.$route.query.status || '').trim().toUpperCase();
            if (s === 'COMPLETED') return 'completed';
            if (s === 'WIN') return 'active';
            return 'files';
        },
        totalCount() { return this.items.length; },
        completedCount() { return this.items.filter(i => !!i.completed).length; },
        progressPercent() {
            if (!this.totalCount) return 0;
            return Math.round((this.completedCount / this.totalCount) * 100);
        },
        isAdmin() {
            // Same rule as everywhere else (admin or superadmin).
            return userIsAdmin();
        },
    },
    mounted() {
        this.fetchDeliverables();
        this.fetchReferencePoints();
    },
    methods: {
        authHeaders() {
            let token = '';
            if (typeof this.getAccessToken === 'function') {
                token = this.getAccessToken();
            } else {
                try {
                    const info = JSON.parse(localStorage.getItem('user-info'));
                    token = info && (info.access_token || info.token);
                } catch (e) { /* ignore */ }
            }
            return { Authorization: 'Bearer ' + token };
        },
        async fetchDeliverables() {
            if (!this.pdfId) { this.loading = false; return; }
            this.loading = true;
            this.loadError = '';
            try {
                const res = await axios.get(
                    this.$API_URL + '/user/deliverables?pdf_id=' + this.pdfId,
                    { headers: this.authHeaders() }
                );
                const data = res.data || {};
                this.items = Array.isArray(data.items) ? data.items : [];
                this.grouped = data.grouped || {};
            } catch (err) {
                console.error('Failed to load deliverables', err);
                this.loadError = 'Couldn’t load deliverables. Check your connection and try again.';
            } finally {
                this.loading = false;
            }
        },
        async fetchReferencePoints() {
            if (!this.pdfId) return;
            try {
                const res = await axios.get(
                    this.$API_URL + '/user/deliverable-reference-points?pdf_id=' + this.pdfId,
                    { headers: this.authHeaders() }
                );
                const list = (res.data && res.data.reference_points) || [];
                // Normalise date to '' for the date input's v-model.
                this.referencePoints = list.map(p => ({
                    key: p.key, label: p.label, date: p.date || '',
                }));
            } catch (err) {
                console.error('Failed to load reference points', err);
            }
        },
        async saveReferencePoints() {
            if (this.savingDates) return;
            this.savingDates = true;
            try {
                // Build {key: 'YYYY-MM-DD' | null} from the entered reference points.
                const dates = {};
                this.referencePoints.forEach(p => { dates[p.key] = p.date || null; });
                const res = await axios.post(
                    this.$API_URL + '/user/set-deliverable-reference-points',
                    { pdf_id: Number(this.pdfId), dates },
                    { headers: this.authHeaders() }
                );
                // Server returns the refreshed (recomputed) deliverables.
                const data = res.data || {};
                this.items = Array.isArray(data.items) ? data.items : this.items;
                this.grouped = data.grouped || this.grouped;
                notify.success('Dates applied. Due dates recalculated.');
            } catch (err) {
                console.error('Failed to apply reference dates', err);
                notify.error('Couldn’t apply the dates. Check them and try again.');
            } finally {
                this.savingDates = false;
            }
        },
        toggleCompleted(item, event) {
            item.completed = event.target.checked;
        },
        async saveItem(item) {
            if (!this.isAdmin) return;   // viewers cannot save deliverable updates
            if (this.savingId !== null) return;
            this.savingId = item.id;
            try {
                await axios.post(
                    this.$API_URL + '/user/update-deliverable',
                    {
                        pdf_id: Number(this.pdfId),
                        id: item.id,
                        completed: !!item.completed,
                        remarks: item.remarks || '',
                        actual_delivery_date: item.actual_delivery_date || null,
                    },
                    { headers: this.authHeaders() }
                );
                notify.success('Deliverable saved');
            } catch (err) {
                console.error('Failed to save deliverable', err);
                notify.error('Couldn’t save the deliverable. Try again.');
            } finally {
                this.savingId = null;
            }
        },
        // --- due-date helpers ---
        daysUntil(item) {
            if (!item.due_date) return null;
            const due = new Date(item.due_date + 'T00:00:00');
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.round((due - today) / (1000 * 60 * 60 * 24));
        },
        isOverdue(item) {
            const d = this.daysUntil(item);
            return !item.completed && d !== null && d < 0;
        },
        isDueSoon(item) {
            const d = this.daysUntil(item);
            return !item.completed && d !== null && d >= 0 && d <= 7;
        },
        dueClass(item) {
            if (item.completed) return 'due-done';
            if (this.isOverdue(item)) return 'due-overdue';
            if (this.isDueSoon(item)) return 'due-soon';
            return '';
        },
        dueNote(item) {
            const d = this.daysUntil(item);
            if (d === null) return '';
            if (d < 0) return `Overdue by ${Math.abs(d)}d`;
            if (d === 0) return 'Due today';
            if (d <= 7) return `In ${d}d`;
            return '';
        },
        formatDate(d) {
            if (!d) return '—';
            const dt = new Date(d + 'T00:00:00');
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        },
        goBack() {
            const back = (window.history.state && window.history.state.back) || '';
            if (back.split('?')[0] === `/bid-details/${this.pdfId}`) {
                this.$router.back();
                return;
            }
            // The Deliverables card lives in the Analysis tab.
            const query = { tab: 'analysis' };
            const status = String(this.$route.query.status || '').trim().toUpperCase();
            if (status) query.status = status;
            else if (this.sidebarActive === 'completed') query.status = 'COMPLETED';
            else if (this.sidebarActive === 'active') query.status = 'WIN';
            this.$router.push({ name: 'BidDetails', params: { pdf_id: this.pdfId }, query });
        },
    },
};
</script>

<style scoped>
.dl-wrapper {
    width: 100%;
    position: relative;
    z-index: 2;
    font-family: var(--font-sans);
}

/* Header */
.dl-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    background: linear-gradient(135deg, #064e3b 0%, #0f766e 60%, #064e3b 100%);
    color: #fff;
    border-radius: 20px;
    padding: 1.6rem 2rem;
    margin-bottom: 1.75rem;
    position: relative;
    overflow: hidden;
}

.dl-header::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 0.12) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.4;
    pointer-events: none;
}

.dl-header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 2;
}

.dl-header-icon {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.16);
    flex-shrink: 0;
}

.dl-title {
    font-family: var(--font-display);
    margin: 0;
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    letter-spacing: -0.02em;
}

.dl-subtitle {
    margin: 0.3rem 0 0;
    font-size: var(--fs-sm);
    line-height: var(--lh-sm);
    color: rgba(255, 255, 255, 0.75);
}

.dl-progress {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
    min-width: 180px;
}

.dl-progress-ring {
    background: rgba(255, 255, 255, 0.18);
    padding: 0.4rem 1rem;
    border-radius: 20px;
}

.dl-progress-num {
    font-size: 0.95rem;
    font-weight: 700;
}

.dl-progress-bar-wrap {
    width: 100%;
    height: 7px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    overflow: hidden;
}

.dl-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #4ade80, #10b981);
    border-radius: 10px;
    transition: width 0.4s ease;
}

.dl-progress-label {
    font-size: 0.72rem;
    opacity: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
}

/* Base dates bar */
.dl-dates-bar {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    background: #f0fdfa;
    border: 1px solid #99f6e4;
    border-radius: 12px;
    padding: 0.9rem 1.2rem;
    margin-bottom: 1.5rem;
}

.dl-dates-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    color: #0f766e;
    font-size: 0.85rem;
    font-weight: 500;
    max-width: 560px;
    text-align: center;
}

.dl-dates-info svg {
    flex-shrink: 0;
}

.dl-dates-fields {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 0.9rem;
    flex-wrap: wrap;
}

.dl-date-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.72rem;
    font-weight: 700;
    color: #0f766e;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.dl-date-field input {
    border: 1px solid #5eead4;
    border-radius: 8px;
    padding: 0.45rem 0.6rem;
    font-size: 0.85rem;
    color: #134e4a;
    background: #fff;
    font-family: var(--font-sans);
}

.dl-date-field input:disabled {
    background: #f1f5f9;
    color: #64748b;
}

.dl-date-save {
    background: #047857;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.5rem 1rem;
    font-weight: 700;
    font-size: 0.82rem;
    cursor: pointer;
    height: 34px;
}

.dl-date-save:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Loading / empty */
.dl-loading,
.dl-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 4rem 0;
    color: #64748b;
    font-weight: 600;
}

.dl-empty {
    flex-direction: row;
    gap: 0.5rem;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    color: #64748b;
}

.dl-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #0f766e;
    border-radius: 50%;
    animation: dl-spin 1s linear infinite;
}

/* Groups */
.dl-group {
    margin-bottom: 1.75rem;
}

.dl-group-title {
    font-size: 0.95rem;
    font-weight: 800;
    color: #0f766e;
    margin: 0 0 0.75rem;
    padding-left: 0.5rem;
    border-left: 4px solid #10b981;
}

/* Table */
.dl-table {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.dl-table-head,
.dl-row {
    display: grid;
    grid-template-columns: 50px 1.7fr 92px 140px 130px 1.1fr 92px;
    gap: 0.8rem;
    align-items: center;
}

.dl-actual-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.4rem 0.5rem;
    font-size: 0.8rem;
    font-family: var(--font-sans);
    color: #334155;
    background: #f8fafc;
}

.dl-actual-input:focus {
    outline: none;
    border-color: #0f766e;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.1);
}

.dl-table-head {
    padding: 0.4rem 1.1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
}

.dl-row {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 16px;
    padding: 0.9rem 1.1rem;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
    transition: all 0.2s;
}

.dl-row:hover {
    border-color: rgba(16, 185, 129, 0.3);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.07);
}

.dl-row.is-done {
    background: #f0fdf4;
    border-color: rgba(16, 185, 129, 0.35);
}

.dl-row.is-overdue:not(.is-done) {
    background: #fef2f2;
    border-color: rgba(239, 68, 68, 0.35);
}

.dl-row.is-soon:not(.is-done):not(.is-overdue) {
    background: #fffbeb;
    border-color: rgba(245, 158, 11, 0.35);
}

/* Checkbox */
.dl-checkbox {
    position: relative;
    display: inline-flex;
    cursor: pointer;
}

.dl-checkbox input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}

.dl-checkmark {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    border: 2px solid #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: all 0.2s;
    background: #fff;
}

.dl-checkmark svg {
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.2s;
}

.dl-checkbox input:checked+.dl-checkmark {
    background: #047857;
    border-color: #047857;
}

.dl-empty.is-error {
    flex-wrap: wrap;
    color: #b42318;
    border-color: #fecdca;
    background: #fef3f2;
}

.dl-retry {
    padding: 0.45rem 0.9rem;
    border-radius: 10px;
    border: 1px solid #b42318;
    background: #fff;
    color: #b42318;
    font-weight: 600;
    font-size: 0.8rem;
    cursor: pointer;
}

.dl-checkbox input:checked+.dl-checkmark svg {
    opacity: 1;
    transform: scale(1);
}

/* Head */
.col-head {
    text-align: left;
    justify-self: start;
}

.dl-head-text {
    display: block;
    width: 100%;
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.45;
    text-align: left;
}

/* Deadline chip */
.dl-chip {
    font-size: 0.75rem;
    font-weight: 700;
    color: #0f766e;
    background: rgba(15, 118, 110, 0.1);
    padding: 3px 9px;
    border-radius: 8px;
    white-space: nowrap;
}

/* Due date */
.dl-due {
    display: flex;
    flex-direction: column;
    font-size: 0.8rem;
    font-weight: 700;
    color: #475569;
}

.dl-due.muted {
    color: #64748b;
    font-weight: 500;
}

.dl-due-note {
    font-size: 0.68rem;
    font-weight: 700;
    margin-top: 1px;
}

.dl-due.due-overdue {
    color: #dc2626;
}

.dl-due.due-soon {
    color: #d97706;
}

.dl-due.due-done {
    color: #16a34a;
}

/* Remarks */
.dl-remarks-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 0.5rem 0.7rem;
    font-size: 0.82rem;
    font-family: var(--font-sans);
    color: #334155;
    resize: vertical;
    background: #f8fafc;
    transition: all 0.2s;
}

.dl-remarks-input:focus {
    outline: none;
    border-color: #0f766e;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.1);
}

/* Save */
.col-action {
    display: flex;
    justify-content: flex-end;
}

.dl-save-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.55rem 0.9rem;
    border: none;
    border-radius: 10px;
    background: #047857;
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.25);
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.dl-save-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.05);
}

.dl-save-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.dl-spin {
    animation: dl-spin 1s linear infinite;
}

/* Footer */
.dl-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 1.75rem;
    padding-top: 1.25rem;
    border-top: 1px dashed #eef2f7;
    flex-wrap: wrap;
}

.dl-back {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 0.55rem 1rem;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-sans);
}

.dl-back:hover {
    background: #f1f5f9;
    transform: translateX(-3px);
}

@keyframes dl-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
    .dl-table-head {
        display: none;
    }

    .dl-row {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .col-action {
        justify-content: flex-start;
    }
}
</style>
